import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, ViewChild, signal, AfterViewInit, OnDestroy, inject, Output, EventEmitter, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { FileType } from '@newroom-connect/library/enums';
import { FileHelper } from '@newroom-connect/library/helpers';
import { IFile, IAreaElement } from '@newroom-connect/library/interfaces';
import { LoggingService } from '@newroom-connect/library/services';
import { BoundingBox } from '@newroom-connect/library/types';

import { AreaElementComponent } from '../../area-element/area-element.component';
import { MarzipanoSceneComponent } from '../../../cubemap-marzipano/components/marzipano-scene/marzipano-scene.component';
import { CubemapFaces } from '../../../cubemap-marzipano/models/marzipano-cubemap-loader.model';
import { MarzipanoScene } from '../../../cubemap-marzipano/models/marzipano-scene.model';
import { SceneHotspotDirective } from '../../../cubemap-marzipano/directives/scene-hotspot.directive';
import { ISelectedAreaElementConfig } from '../area-background.component';

@Component({
  selector: 'nrc-area-background-360-degree',
  standalone: true,
  imports: [MarzipanoSceneComponent, CommonModule, SceneHotspotDirective, AreaElementComponent],
  templateUrl: './area-background-360-degree.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AreaBackground360DegreeComponent implements OnChanges, AfterViewInit, OnDestroy {
  private readonly loggingService = inject(LoggingService);

  @ViewChild(MarzipanoSceneComponent) private marzipanoSceneComponent!: MarzipanoSceneComponent;

  @Input({ required: true }) public apiBaseUrl = '';
  @Input({ required: true }) public backgroundFile!: IFile;

  @Input() public areaElements?: IAreaElement[];
  @Input() public parentContainer?: ElementRef<HTMLDivElement>;
  @Input() public currentLanguageCode?: string;

  @Input() public selectedAreaElementConfig : ISelectedAreaElementConfig | undefined;

  @Output() public areaElementBoundingBoxChange = new EventEmitter<{id: string, boundingBox: BoundingBox}>();
  @Output() public areaElementClickedEvent = new EventEmitter<IAreaElement>();
  @Output() public areaElementDblClickedEvent = new EventEmitter<IAreaElement>();
  @Output() public areaElementClickOutsideEvent = new EventEmitter<void>();

  public cubemapFacesSig = signal<CubemapFaces | null>(null);
  public marzipanoScene: MarzipanoScene | null = null;

  private lastCubemapFacesHash?: string;

  private readonly destroy$ = new Subject<void>();

  /**
   * Initialize the area element adapter after view initialization.
   */
  public ngAfterViewInit(): void {
    // Initialize the adapter when the scene is ready
    if (this.marzipanoSceneComponent) {
      this.marzipanoSceneComponent.sceneReady.subscribe((scene: MarzipanoScene) => {
        this.marzipanoScene = scene;
      });
    }
  }

  /**
   * Clean up resources when component is destroyed.
   */
  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle input changes, particularly background file updates.
   * Only update cubemap faces if the actual face URLs have changed.
   *
   * @param changes - The changes to component inputs.
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['backgroundFile']) {
      if (changes['backgroundFile'].currentValue) {
        try {
          const newFaces = this.generateCubemapFacesFromFile(changes['backgroundFile'].currentValue);
          const newFacesHash = this.generateFacesHash(newFaces);

          // Only update if the face URLs actually changed
          if (this.lastCubemapFacesHash !== newFacesHash) {
            this.lastCubemapFacesHash = newFacesHash;
            this.cubemapFacesSig.set(newFaces);
          }
        } catch (error) {
          this.loggingService.error(error);
          this.lastCubemapFacesHash = undefined;
          this.cubemapFacesSig.set(null);
        }
      } else {
        this.lastCubemapFacesHash = undefined;
        this.cubemapFacesSig.set(null);
      }
    }
  }

  /**
   * Generate cubemap faces from the provided file having an image URL for each face.
   * A face map consists of 6 different faces (px, py, pz, nx, ny, nz) which are used to create a cubemap.
   *
   * @param file The file to create the cubemap faces from.
   *
   * @returns The generated cubemap faces.
   *
   * @throws Error, If not every face contains an image URL.
   */
  private generateCubemapFacesFromFile(file: IFile): CubemapFaces {
    const faceMap: CubemapFaces = {
      px: FileHelper.getFileSourceURI(this.apiBaseUrl, file.formats?.find(fileFormat => fileFormat.type === FileType.CUBEMAP_FACE_PX)),
      py: FileHelper.getFileSourceURI(this.apiBaseUrl, file.formats?.find(fileFormat => fileFormat.type === FileType.CUBEMAP_FACE_PY)),
      pz: FileHelper.getFileSourceURI(this.apiBaseUrl, file.formats?.find(fileFormat => fileFormat.type === FileType.CUBEMAP_FACE_PZ)),
      nx: FileHelper.getFileSourceURI(this.apiBaseUrl, file.formats?.find(fileFormat => fileFormat.type === FileType.CUBEMAP_FACE_NX)),
      ny: FileHelper.getFileSourceURI(this.apiBaseUrl, file.formats?.find(fileFormat => fileFormat.type === FileType.CUBEMAP_FACE_NY)),
      nz: FileHelper.getFileSourceURI(this.apiBaseUrl, file.formats?.find(fileFormat => fileFormat.type === FileType.CUBEMAP_FACE_NZ))
    };

    if (!Object.values(faceMap).every(faceUrl => !!faceUrl)) {
      throw new Error('Unable to generate cubemap face map from file: Not every face contains an image URL.');
    }

    return faceMap;
  }

  /**
   * Generate a hash from cubemap faces for comparison.
   * This allows us to detect when face URLs actually change.
   *
   * @param faces The cubemap faces to hash.
   * @returns A hash string representing the face URLs.
   */
  private generateFacesHash(faces: CubemapFaces): string {
    const faceUrls = [faces.px, faces.py, faces.pz, faces.nx, faces.ny, faces.nz];

    return faceUrls.join('|');
  }

  /**
   * Handle scene ready event from marzipano-scene component.
   * This replaces the complex adapter initialization with simple directive approach.
   *
   * @param scene - The initialized Marzipano scene.
   */
  public onSceneReady(scene: MarzipanoScene): void {
    this.marzipanoScene = scene;
    this.loggingService.info('Marzipano scene ready for area hotspots');
  }

  /**
   * Forward bounding box changes from hotspot directive to parent listeners.
   * Also updates the area element's bounding box to ensure state consistency.
   *
   * @param areaElement - The area element being updated.
   * @param payload - The bounding box change payload.
   * @param payload.hotspotId
   * @param payload.boundingBox
   */
  public onHotspotBoundingBoxChanged(areaElement: IAreaElement, payload: { hotspotId: string; boundingBox: BoundingBox }): void {
    if (areaElement.id !== payload.hotspotId || !areaElement.boundingBox.spherical) {
      return;
    }

    // Update the area element's bounding box with the merged data from the directive
    // This ensures that both drag and transform changes are properly persisted
    areaElement.boundingBox = {
      ...areaElement.boundingBox,
      ...payload.boundingBox,
      // Ensure spherical data is properly merged (handle case where it might not exist)
      spherical: {
        ...areaElement.boundingBox.spherical,
        ...payload.boundingBox.spherical
      },
      // Ensure perspective data is properly merged (handle case where it might not exist)
      perspective: {
        ...areaElement.boundingBox.perspective,
        ...payload.boundingBox.perspective
      }
    };

    // Emit the change to parent components for further processing/persistence
    this.areaElementBoundingBoxChange.emit({ id: payload.hotspotId, boundingBox: areaElement.boundingBox });
  }

  /**
   * Track function for Angular's @for loop that combines element ID and index position.
   * This prevents insertBefore issues when elements are reordered within the array.
   *
   * @param index - The current index of the element in the array.
   * @param areaElement - The area element being tracked.
   * @returns A unique tracking identifier combining ID and position.
   */
  public trackByIdAndIndex(index: number, areaElement: IAreaElement): string {
    return `${areaElement.id}-${index}`;
  }
}
